import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
  downloadSampleRequestPdf,
  getCustomerData,
  getSampleRequestDetailsList,
  getSampleRequestList
} from 'Service/ApiService';
import Loader from 'utils/Loader';
import { showAlert } from 'utils/ErrorMessage';

const textFieldProps = {
  InputLabelProps: {
    shrink: true
  }
};

const searchableDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 4,
    borderColor: state.isFocused ? '#1976d2' : 'rgba(0, 0, 0, 0.23)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.87)'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    minHeight: 40,
    padding: '0 14px',
    flexWrap: 'nowrap',
    overflow: 'hidden'
  }),
  singleValue: (provided) => ({
    ...provided,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.6)',
    whiteSpace: 'nowrap'
  }),
  option: (provided) => ({
    ...provided,
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.54)',
    paddingRight: 12
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1300
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1600
  })
};

const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;

const searchableFieldWrapperSx = {
  position: 'relative'
};

const searchableFieldLabelSx = {
  position: 'absolute',
  top: -8,
  left: 12,
  px: 0.5,
  backgroundColor: '#fff',
  color: 'rgba(0, 0, 0, 0.6)',
  fontSize: '0.75rem',
  lineHeight: 1,
  zIndex: 1
};

const headerCellSx = {
  px: 1.25,
  py: 1.25,
  fontSize: '0.78rem',
  fontWeight: 700,
  whiteSpace: 'nowrap'
};

const bodyCellSx = {
  px: 1.25,
  py: 1,
  fontSize: '0.78rem',
  verticalAlign: 'top',
  whiteSpace: 'normal',
  wordBreak: 'break-word'
};

const isSuccessResponse = (response) => response?.success === true || response?.success === 'true';

const getSearchableNoOptionsMessage = ({ inputValue }) => {
  if (String(inputValue || '').trim().length < 3) {
    return 'Type at least 3 characters';
  }

  return 'No data found';
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const normalizedValue = String(value).split(' ')[0];
  const date = new Date(`${normalizedValue}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const mapPartyOptions = (customerList = []) => customerList.reduce((uniqueList, customer) => {
  const partyCode = customer?.partyCode || customer?.value || '';

  if (!partyCode || uniqueList.some((item) => item.value === partyCode)) {
    return uniqueList;
  }

  uniqueList.push({
    value: partyCode,
    label: [
      customer?.partyName,
      customer?.city,
      partyCode
    ].filter(Boolean).join(' - ')
  });

  return uniqueList;
}, []);

const ViewSampleRequest = () => {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
  const [filters, setFilters] = useState({
    partyCode: '',
    fromDate: '',
    toDate: ''
  });
  const [sampleRequests, setSampleRequests] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [isPartyLoading, setIsPartyLoading] = useState(false);
  const [partySearchText, setPartySearchText] = useState('');
  const [selectedPartyOption, setSelectedPartyOption] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetails, setRequestDetails] = useState([]);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [downloadingDocNo, setDownloadingDocNo] = useState('');

  const loadSampleRequests = useCallback(async () => {
    try {
      setIsTableLoading(true);
      const response = await getSampleRequestList({
        page: page + 1,
        pageSize: 100,
        userCode: user?.userCode || '',
        partyCode: filters.partyCode,
        fromDate: filters.fromDate,
        toDate: filters.toDate
      });

      if (isSuccessResponse(response)) {
        setSampleRequests(Array.isArray(response?.data) ? response.data : []);
        setTotalRows(Number(response?.totalRows || 0));
      } else {
        setSampleRequests([]);
        setTotalRows(0);
        showAlert(response?.msg || 'Failed to load sample request records.');
      }
    } catch (error) {
      setSampleRequests([]);
      setTotalRows(0);
      showAlert(error?.message || 'Failed to load sample request records.');
    } finally {
      setIsTableLoading(false);
    }
  }, [filters.fromDate, filters.partyCode, filters.toDate, page, user?.userCode]);

  const loadPartyOptions = useCallback(async () => {
    const trimmedSearchText = partySearchText.trim();

    if (trimmedSearchText && trimmedSearchText.length < 3) {
      setIsPartyLoading(false);
      return;
    }

    try {
      setIsPartyLoading(true);
      const response = await getCustomerData(user?.userCode || '', trimmedSearchText);

      if (isSuccessResponse(response)) {
        setPartyOptions(mapPartyOptions(response?.customerList || []));
      } else {
        setPartyOptions([]);
      }
    } catch (error) {
      setPartyOptions([]);
      showAlert(error?.message || 'Failed to load party list.');
    } finally {
      setIsPartyLoading(false);
    }
  }, [partySearchText, user?.userCode]);

  useEffect(() => {
    const debounceTimer = setTimeout(loadPartyOptions, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [loadPartyOptions]);

  useEffect(() => {
    loadSampleRequests();
  }, [loadSampleRequests]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setPage(0);
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartyFilterChange = (selectedOption) => {
    setSelectedPartyOption(selectedOption || null);
    setPage(0);
    setFilters((prev) => ({
      ...prev,
      partyCode: selectedOption?.value || ''
    }));
  };

  const handleResetFilters = () => {
    setSelectedPartyOption(null);
    setPartySearchText('');
    setPage(0);
    setFilters({
      partyCode: '',
      fromDate: '',
      toDate: ''
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenDetails = async (request) => {
    setSelectedRequest(request);
    setRequestDetails([]);

    try {
      setIsDetailsLoading(true);
      const response = await getSampleRequestDetailsList(request?.samReqOrderId);

      if (isSuccessResponse(response)) {
        setRequestDetails(Array.isArray(response?.data) ? response.data : []);
      } else {
        showAlert(response?.msg || 'Failed to load sample request details.');
      }
    } catch (error) {
      showAlert(error?.message || 'Failed to load sample request details.');
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedRequest(null);
    setRequestDetails([]);
  };

  const handleDownloadRequest = async (request) => {
    const docNo = request?.samReqDocNo || '';

    if (!docNo) {
      showAlert('Document number not found.');
      return;
    }

    try {
      setDownloadingDocNo(docNo);
      await downloadSampleRequestPdf(docNo);
    } catch (error) {
      showAlert(error?.message || 'Failed to download sample request document.');
    } finally {
      setDownloadingDocNo('');
    }
  };

  return (
    <div className="bg" style={{ display: 'block' }}>
      <div className="cutrange" style={{ width: '100%',  maxWidth: '100%' }}>
        <span className="font20">SAMPLE REQUEST</span>
      </div>

      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          p: { xs: 1, md: 1, xl: 2 },
          borderRadius: 2.5
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5">Sample Request</Typography>
            
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/sample-request-dashboard')}
            size="small"
            sx={{
              textTransform: 'none',
              alignSelf: { xs: 'flex-end', sm: 'center' },
              width: 'fit-content',
              minWidth: 130,
              ml: { xs: 'auto', sm: 0 },
              whiteSpace: 'nowrap',
              px: 0.75,
              py: 0.75,
              flexShrink: 0
            }}
          >
            Add Sample Request
          </Button>
        </Stack>

        <Box
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 2.5,
            mb: 0.5,
            p: { xs: 1.5, md: 2.5 }
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={6} lg={3} xl={3}>
              <Box sx={searchableFieldWrapperSx}>
                <Typography variant="body2" sx={searchableFieldLabelSx}>
                  Party Name
                </Typography>
                <Select
                  value={selectedPartyOption}
                  placeholder="All Parties"
                  isSearchable
                  isClearable
                  isLoading={isPartyLoading}
                  options={partyOptions}
                  styles={searchableDropdownStyles}
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition="fixed"
                  noOptionsMessage={getSearchableNoOptionsMessage}
                  onInputChange={(inputValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                      setPartySearchText(inputValue);
                    }
                    if (actionMeta.action === 'menu-close' && !inputValue) {
                      setPartySearchText('');
                    }
                  }}
                  onChange={handlePartyFilterChange}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6} lg={3} xl={3}>
              <TextField
                fullWidth
                label="From Date"
                name="fromDate"
                type="date"
                value={filters.fromDate}
                onChange={handleFilterChange}
                {...textFieldProps}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3} xl={3}>
              <TextField
                fullWidth
                label="To Date"
                name="toDate"
                type="date"
                value={filters.toDate}
                onChange={handleFilterChange}
                {...textFieldProps}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2} xl={1.5}>
              <Button fullWidth variant="outlined" onClick={handleResetFilters} sx={{ minHeight: 40, textTransform: 'none' }}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 2.5,
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {isTableLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              <Loader />
            </Box>
          ) : null}
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                width: '100%',
                maxHeight: 520,
                overflow: 'auto'
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{
                  minWidth: 980,
                  '& .MuiTableCell-root': {
                    borderBottomColor: 'rgba(224, 224, 224, 0.8)'
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Doc No</TableCell>
                    <TableCell sx={headerCellSx}>Party Name</TableCell>
                    <TableCell sx={headerCellSx}>Conference Name</TableCell>
                    <TableCell sx={headerCellSx}>Category</TableCell>
                    <TableCell sx={headerCellSx}>Courier No</TableCell>
                    <TableCell sx={headerCellSx}>Memo No</TableCell>
                    <TableCell sx={headerCellSx}>Memo Date</TableCell>
                    <TableCell sx={headerCellSx}>Remark</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isTableLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                        Loading data...
                      </TableCell>
                    </TableRow>
                  ) : sampleRequests.length > 0 ? (
                    sampleRequests.map((request) => (
                      <TableRow key={request.samReqOrderId || request.samReqDocNo} hover>
                        <TableCell sx={{ ...bodyCellSx, minWidth: 125 }}>
                          <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-start">
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => handleOpenDetails(request)}
                              sx={{
                                textTransform: 'none',
                                minWidth: 'auto',
                                p: 0,
                                color: '#1976d2',
                                textDecoration: 'underline',
                                fontWeight: 600
                              }}
                            >
                              {request.samReqDocNo || '-'}
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadRequest(request)}
                              title="Download document"
                              disabled={downloadingDocNo === request.samReqDocNo}
                              sx={{
                                width: 28,
                                height: 28,
                                p: 0.25,
                                color: '#b00000'
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, minWidth: 190 }}>{request.partyName || '-'} - {request.partyCode || '-'}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, minWidth: 180 }}>{request.name || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{request.sampleCategory || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{request.samReqCourierNo || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{request.samReqMemoNo || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{formatDate(request.samReqMemoDate)}</TableCell>
                        <TableCell sx={{ ...bodyCellSx, minWidth: 140 }}>{request.samReqOrderRemark || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                        No data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              rowsPerPage={100}
              rowsPerPageOptions={[]}
              onPageChange={handleChangePage}
            />
          </CardContent>
        </Box>
      </Card>

      <Dialog
        open={Boolean(selectedRequest)}
        onClose={handleCloseDetails}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-container': {
            justifyContent: { xs: 'center', md: 'flex-start' }
          },
          '& .MuiDialog-paper': {
            width: { xs: 'calc(100% - 32px)', md: 'calc(100% - 250px - 64px)' },
            maxWidth: { xs: 'calc(100% - 32px)', md: 'none' },
            ml: { xs: 2, md: 'calc(250px + 32px)' },
            mr: { xs: 2, md: 4 }
          }
        }}
      >
        <DialogTitle>View Sample Request Details</DialogTitle>
        <DialogContent>
          {isDetailsLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <Loader />
            </Box>
          ) : (
            <TableContainer sx={{ pt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Material Code</TableCell>
                    <TableCell sx={headerCellSx}>Material Desc</TableCell>
                    <TableCell sx={headerCellSx}>Main Group</TableCell>
                    <TableCell sx={headerCellSx}>Barcode</TableCell>
                    <TableCell sx={headerCellSx}>Sample Type</TableCell>
                    <TableCell sx={headerCellSx}>Vertical</TableCell>
                    <TableCell sx={headerCellSx}>Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestDetails.length > 0 ? (
                    requestDetails.map((item) => (
                      <TableRow key={item.sampleReqOrderDtlsId || `${item.samReqOrderId}-${item.materialCode}`}>
                        <TableCell sx={bodyCellSx}>{item.materialCode || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{item.materialDesc || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{item.mainGroup || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{item.barcode || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{item.sampleType || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{item.materialVertical || '-'}</TableCell>
                        <TableCell sx={bodyCellSx}>{item.quantity || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                        No details found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetails} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ViewSampleRequest;
